import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import iconExcellent from "../assets/mood/icon-excellent.png";
import iconGood from "../assets/mood/icon-good.png";
import iconOkay from "../assets/mood/icon-okey.png";
import iconSad from "../assets/mood/icon-sad.png";
import iconTerrible from "../assets/mood/icon-terrible.png";
import "./Theme.css";
import "./EmotionalMoodToday.css";

const MOOD_ICON = {
  excellent: iconExcellent,
  good: iconGood,
  okay: iconOkay,
  sad: iconSad,
  terrible: iconTerrible,
};

const REASONS = [
  "emotionalMood.reasons.family",
  "emotionalMood.reasons.relationship",
  "emotionalMood.reasons.friends",
  "emotionalMood.reasons.food",
  "emotionalMood.reasons.health",
  "emotionalMood.reasons.exercise",
  "emotionalMood.reasons.work",
  "emotionalMood.reasons.spirituality",
  "emotionalMood.reasons.studies",
  "emotionalMood.reasons.sleep",
  "emotionalMood.reasons.travel",
  "emotionalMood.reasons.finance",
];

const EMOTIONS = {
  excellent: [
    "emotionalMood.emotions.ecstatic",
    "emotionalMood.emotions.proud",
    "emotionalMood.emotions.jubilant",
    "emotionalMood.emotions.fulfilled",
    "emotionalMood.emotions.elated",
  ],
  good: [
    "emotionalMood.emotions.happy",
    "emotionalMood.emotions.excited",
    "emotionalMood.emotions.grateful",
    "emotionalMood.emotions.confident",
    "emotionalMood.emotions.relaxed",
  ],
  okay: [
    "emotionalMood.emotions.content",
    "emotionalMood.emotions.acceptance",
    "emotionalMood.emotions.satisfaction",
    "emotionalMood.emotions.tolerance",
    "emotionalMood.emotions.indifference",
  ],
  sad: [
    "emotionalMood.emotions.disappointment",
    "emotionalMood.emotions.frustration",
    "emotionalMood.emotions.irritation",
    "emotionalMood.emotions.envy",
    "emotionalMood.emotions.embarrassment",
  ],
  terrible: [
    "emotionalMood.emotions.despair",
    "emotionalMood.emotions.guilt",
    "emotionalMood.emotions.regret",
    "emotionalMood.emotions.shame",
    "emotionalMood.emotions.powerlessness",
  ],
};

const EmotionalMoodToday = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const selectedMood = urlParams.get("selectedMood") || "good";
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";

  const [selectedReasons, setSelectedReasons] = useState([]);
  const [selectedEmotion, setSelectedEmotion] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  const toggleReason = (key) => {
    setSelectedReasons((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleEmotion = (key) => {
    setSelectedEmotion((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const hasAny = selectedReasons.length > 0 || selectedEmotion.length > 0;

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const handleSubmit = () => {
    if (submitting) return;
    setSubmitting(true);

    const resolvedReasons = selectedReasons.map((k) => t(k));
    const resolvedEmotions = selectedEmotion.map((k) => t(k));

    lifestyleApi
      .logMood({
        baseEmotion: selectedMood,
        commonReason: JSON.stringify(resolvedReasons),
        anythingElse: JSON.stringify(resolvedEmotions),
      })
      .then((res) => {
        if (String(res?.data?.code) === "200") {
          const encodedPayload = encodeURIComponent(
            JSON.stringify({
              url: `https://lifestyle1.goqii.com/emotional-wellbeing?${buildQs()}`,
              title: "Emotional Wellbeing",
              dismiss: "Y",
            })
          );
          window.location.href = `https://goqii.com/app?fai=${encodedPayload}`;
        }
      })
      .catch((err) => console.error("logMood:", err))
      .finally(() => setSubmitting(false));
  };

  const emotionList = EMOTIONS[selectedMood] || EMOTIONS.good;
  const moodIcon = MOOD_ICON[selectedMood] || MOOD_ICON.good;

  return (
    <>
      <section className="emt-page">
        <div className="emt-container">
          <div className="emt-icon-wrap">
            <img src={moodIcon} className="emt-icon" alt={selectedMood} />
          </div>
          <p className="emt-mood-name">{t(`emotionalMood.moodNames.${selectedMood}`)}</p>

          <div className="emt-question-block">
            <label className="emt-label">{t("emotionalMood.reasonTitle")}</label>
            <div className="emt-chips">
              {REASONS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`emt-chip${selectedReasons.includes(key) ? " active" : ""}`}
                  onClick={() => toggleReason(key)}
                >
                  {t(key)}
                </button>
              ))}
            </div>
          </div>

          <div className="emt-question-block">
            <label className="emt-label">{t("emotionalMood.anythingElseTitle")}</label>
            <div className="emt-chips">
              {emotionList.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`emt-chip${selectedEmotion.includes(key) ? " active" : ""}`}
                  onClick={() => toggleEmotion(key)}
                >
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="emt-bottom-btn">
        <button
          type="button"
          className="emt-btn-done"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {hasAny ? t("emotionalMood.doneBtn") : t("emotionalMood.logBtn")}
        </button>
      </section>
    </>
  );
};

export default EmotionalMoodToday;
